import { Injector } from "@angular/core";
import { SMTDataListsService } from "../data-lists.service";
import { IntegrationService } from './integration-service';
import {
  RRHH_INTEGRATION_SERVICE,
  SIGN_INTEGRATION_SERVICE,
  SIGNAFORM_INTEGRATION_SERVICE
} from '../../models/alden-constants';
import { ShareDataRow } from "@alfresco/adf-content-services";
import { Node, NodeChildAssociationEntry } from "@alfresco/js-api";
import { isLibrary, isLocked } from "@alfresco/aca-shared";
import { ThumbnailService } from "@alfresco/adf-core";

export abstract class IntegrationServiceAbstract implements IntegrationService {

  constructor(protected dataListsService: SMTDataListsService, protected thumbnailService: ThumbnailService, protected injector?: Injector) { }

  getDataListElements(key: string, dataListName: string): Promise<any> {
    // Plugins
    const signaformDataListElements = this.pluginGetDataListElements(key, dataListName, SIGNAFORM_INTEGRATION_SERVICE);
    if (signaformDataListElements != null)
      return signaformDataListElements;

    // TODO Eliminar bdocs-integration-service-impl
    // const bdocsDataListElements = this.pluginGetDataListElements(key, dataListName, BDOCS_INTEGRATION_SERVICE);
    // if (bdocsDataListElements != null)
    //   return bdocsDataListElements;

    const rrhhDataListElements = this.pluginGetDataListElements(key, dataListName, RRHH_INTEGRATION_SERVICE);
    if (rrhhDataListElements != null)
      return rrhhDataListElements;

    // Por defecto
    return this.dataListsService.getDataListValues(dataListName);
  }

  async getThumbnailImage(row: ShareDataRow): Promise<{ icon: string; tooltip?: string; }[] | null> {
    let images: { icon: string, tooltip?: string }[] = [];

    // Plugins
    const signaformDataListElements = await this.pluginGetThumbnailImage(row, SIGN_INTEGRATION_SERVICE);
    if (signaformDataListElements != null)
      images = images.concat(signaformDataListElements);

    if (images.length > 0)
      return images;

    // Por defecto
    const node: NodeChildAssociationEntry = row?.node;
    if (this.isLocked(node))
      return [{ icon: 'assets/images/baseline-lock-24px.svg' }];
    if (this.isLibrary(node))
      return [{ icon: 'assets/images/baseline-library_books-24px.svg' }];
    if (node?.entry.isFolder)
      return [{ icon: 'assets/images/ft_ic_folder.svg' }];
    if (node?.entry.content) // Para /shared
      return [{ icon: this.thumbnailService.getMimeTypeIcon(node.entry.content.mimeType) }];
    return [{ icon: 'assets/images/ft_ic_miscellaneous.svg' }];
  }

  /**
   * Obtiene los elementos de un Data List mediante un servicio externo inyectado dinámicamente.
   *
   * Este método permite delegar la obtención de elementos de un Data List en un
   * servicio externo identificado por `serviceToken`. Utiliza el injector para
   * obtener la instancia del servicio y ejecutar su método `getDataListElements`.
   *
   * Si el servicio no puede obtenerse o se produce un error durante la ejecución,
   * se captura la excepción y se devuelve `null` para evitar que el flujo falle.
   *
   * @param key Clave asociada a la consulta del Data List.
   * @param dataListName Nombre del Data List cuyos elementos deben obtenerse.
   * @param serviceToken Token del servicio que implementa la lógica personalizada.
   * @returns Promise con los elementos obtenidos o `null` si no se pudo ejecutar.
   */
  protected pluginGetDataListElements(key: string, dataListName: string, serviceToken: any): Promise<any> {
    try {
      const service = this.injector.get<any>(serviceToken);
      return service.getDataListElements(key, dataListName);
    } catch (e) {
      return null;
    }
  }

  /**
   * Obtiene miniaturas personalizadas para un documento mediante un servicio externo.
   *
   * Este método delega la generación de miniaturas (iconos, imágenes o indicadores)
   * en un servicio externo identificado por `serviceToken`. Utiliza el injector para
   * obtener la instancia del servicio y ejecutar su método `getThumbnailImage`.
   *
   * Si el servicio no existe o no puede obtenerse, se captura la excepción y se
   * devuelve `null` para evitar errores en el renderizado de la tabla.
   *
   * @param row Fila del documento para el cual se solicitan las miniaturas.
   * @param serviceToken Token del servicio que implementa la lógica de miniaturas.
   * @returns Promise con la lista de miniaturas o `null` si no se pudo ejecutar.
   */
  protected pluginGetThumbnailImage(row: ShareDataRow, serviceToken: any): Promise<{ icon: string; tooltip?: string; }[] | null> {
    try {
      const service = this.injector.get<any>(serviceToken);
      return service.getThumbnailImage(row);
    } catch (e) {
      return null;
    }
  }

  protected isLocked(node?: { entry: Node }): boolean {
    if (!node?.entry)
      return false;
    return isLocked(node);
  }

  protected isLibrary(node?: { entry: Node }): boolean {
    if (!node?.entry)
      return false;
    return isLibrary(node);
  }

}
